import { useState, useRef, useEffect, useCallback } from 'react';

export const CameraErrorType = {
  NOT_ALLOWED: 'NOT_ALLOWED',
  NOT_FOUND: 'NOT_FOUND',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
  BUSY: 'BUSY',
  UNKNOWN: 'UNKNOWN'
};

export function useCameraStream() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // default to rear camera

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.warn('Error stopping camera track:', e);
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  }, []);

  const startStream = useCallback(async (preferredFacing = facingMode) => {
    stopStream();
    setError(null);
    setErrorType(null);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera access is not supported by this browser.');
      setErrorType(CameraErrorType.NOT_SUPPORTED);
      return;
    }

    try {
      const constraints = {
        video: {
          facingMode: { ideal: preferredFacing },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;
        await videoRef.current.play();
        setIsReady(true);
      }
    } catch (err) {
      console.error('Camera stream error:', err);
      let type = CameraErrorType.UNKNOWN;
      let msg = 'Failed to access camera.';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        type = CameraErrorType.NOT_ALLOWED;
        msg = 'Camera permission was denied. Please allow camera access in your browser settings or upload from device.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        type = CameraErrorType.NOT_FOUND;
        msg = 'No camera found on this device. You can upload an image directly from your device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        type = CameraErrorType.BUSY;
        msg = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        // Fallback without specific constraints
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          streamRef.current = fallbackStream;
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            await videoRef.current.play();
            setIsReady(true);
            return;
          }
        } catch (fallbackErr) {
          type = CameraErrorType.NOT_SUPPORTED;
          msg = 'Could not initialize camera with requested configuration.';
        }
      }

      setError(msg);
      setErrorType(type);
      setIsReady(false);
    }
  }, [facingMode, stopStream]);

  const switchFacingMode = useCallback(() => {
    const nextMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(nextMode);
    startStream(nextMode);
  }, [facingMode, startStream]);

  const captureFrame = useCallback(() => {
    return new Promise((resolve, reject) => {

      const video = videoRef.current;
      if (!video || !isReady) {
        resolve(null);
        return;
      }

      const canvas = document.createElement('canvas');
      const w = video.videoWidth || 1280;
      const h = video.videoHeight || 720;
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas 2D context'));
        return;
      }

      ctx.drawImage(video, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.90);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ blob, dataUrl, width: w, height: h });
          } else {
            reject(new Error('Failed to create image blob from camera capture'));
          }
        },
        'image/jpeg',
        0.90
      );
    });
  }, [isReady]);

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  return {
    videoRef,
    isReady,
    error,
    errorType,
    facingMode,
    startStream,
    stopStream,
    switchFacingMode,
    captureFrame
  };
}
